import { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";

function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const res = await axiosInstance.get("/api/documents");
      setDocuments(res.data);
    } catch (err) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setTitle("");
    setCategory("");
    setFile(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("category", category);
    formData.append("file", file);

    setSaving(true);
    try {
      await axiosInstance.post("/api/documents", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      setShowModal(false);
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.error || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await axiosInstance.delete(`/api/documents/${id}`);
      toast.success("Document deleted");
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete");
    }
  };

  const fileIcon = (path) => {
    const ext = path?.split(".").pop()?.toLowerCase();
    if (ext === "pdf") return "bi-file-earmark-pdf text-danger";
    if (["doc", "docx"].includes(ext))
      return "bi-file-earmark-word text-primary";
    if (["xls", "xlsx"].includes(ext))
      return "bi-file-earmark-excel text-success";
    return "bi-file-earmark text-muted";
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Documents</h4>
        <button className="btn btn-primary" onClick={openModal}>
          <i className="bi bi-plus-lg me-1"></i> Upload Document
        </button>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "50px" }}></th>
                <th>Title</th>
                <th className="d-none d-md-table-cell">Category</th>
                <th className="d-none d-md-table-cell">Size</th>
                <th style={{ width: "100px" }} className="text-end">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {documents.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-4">
                    No documents uploaded yet.
                  </td>
                </tr>
              ) : (
                documents.map((doc) => (
                  <tr key={doc.id}>
                    <td>
                      <i className={`bi ${fileIcon(doc.file_path)} fs-5`}></i>
                    </td>
                    <td>
                      <a
                        href={`${process.env.REACT_APP_API_URL}/${doc.file_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none fw-medium"
                      >
                        {doc.title}
                      </a>
                    </td>
                    <td className="d-none d-md-table-cell text-muted small">
                      {doc.category || "—"}
                    </td>
                    <td className="d-none d-md-table-cell text-muted small">
                      {doc.file_size_kb ? `${doc.file_size_kb} KB` : "—"}
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(doc.id, doc.title)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">Upload Document</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Category</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Fees, Circulars, Syllabus"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                      />
                    </div>
                    <div className="mb-1">
                      <label className="form-label">File</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".pdf,.doc,.docx,.xls,.xlsx"
                        onChange={(e) => setFile(e.target.files[0])}
                        required
                      />
                      <small className="text-muted">
                        PDF, Word, or Excel files
                      </small>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? "Uploading..." : "Upload"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}
    </div>
  );
}

export default DocumentsPage;
